import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Clock, Globe, CheckCircle } from 'lucide-react';
import { CONTACT, METRICS } from '../data/mock';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';

const ContactPage = () => {
  return (
    <div className="min-h-screen pt-20 relative overflow-hidden">
      <SEO 
        title="Initiate Consultation | Elite Full-Stack Engineering Partnership"
        description="Connect with elite full-stack engineering specialists. Strategic consultation, flexible engagement, proven excellence across 150+ enterprise solutions."
        keywords="contact full-stack developers, hire engineering team, software development consultation, outsource development"
        url="/contact"
      />

      {/* Geometric Backgrounds */}
      <div className="fixed inset-0 pointer-events-none opacity-20 z-0">
        <div className="line-structure" style={{ top: '15%', right: '10%', transform: 'rotate(-20deg)' }} />
        <div className="geometric-pattern" style={{ bottom: '20%', left: '15%', animationDelay: '1s' }} />
        
        {/* Decorative Bento Boxes */}
        <div className="bento-box-decoration medium floating" style={{ top: '20%', left: '10%', animationDelay: '1.2s' }} />
        <div className="bento-box-decoration small floating" style={{ bottom: '25%', right: '15%', animationDelay: '3.2s' }} />
        <div className="luxury-gradient-overlay" style={{ top: '30%', right: '18%', width: '300px', height: '300px' }} />
        <div className="corner-bracket bottom-right" style={{ bottom: '20%', right: '10%' }} />
      </div>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-violet-50/50 via-purple-50/30 to-white relative">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-50/30 via-transparent to-violet-50/30 pointer-events-none" />
        <div className="container max-w-6xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-block mb-6">
              <span className="px-5 py-2 rounded-full bg-gradient-to-r from-purple-100 to-violet-100 text-[#4C3362] text-sm font-bold border-2 border-purple-200">
                24-Hour Response Time
              </span>
            </div>
            <h1 className="display-lg mb-8">
              Shape Your Next System With <span className="gradient-text">Experienced Engineers</span>
            </h1>
            <p className="text-xl text-slate-700 font-medium leading-relaxed max-w-4xl mx-auto">
              If you’re exploring a new build or need guidance on an existing one, reach out. We’ll walk through the technical landscape with you and determine the best way forward.
            </p>
          </motion.div>
        </div>
      </section>
      {/* Contact Form Section - Moved to Top */}
      <section className="py-16 bg-white">
        <div className="container max-w-5xl">
          <ContactForm ctaText="Start Your Free Technical Consultation" />
        </div>
      </section>

      {/* Contact Information - Bento Grid */}
      <section className="py-12 bg-white relative">
        <div className="container max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Contact Cards */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="bento-card">
                <h2 className="text-3xl font-bold mb-6 gradient-text">Initiate Consultation</h2>
                <p className="text-slate-700 leading-relaxed mb-6">
                  We respond within 24 hours on business days. Whether you have a detailed specification or only the outline of an idea, we can step in at any stage of the process.
                </p>
              </div>

              <div className="bento-card bg-gradient-to-br from-purple-50 to-violet-50">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#4C3362] to-[#3C284D] flex items-center justify-center shadow-lg flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Direct Communication</h3>
                    <a 
                      href={`mailto:${CONTACT.email}`}
                      className="text-[#4C3362] hover:text-[#3C284D] font-semibold hover:underline text-lg"
                    >
                      {CONTACT.email}
                    </a>
                  </div>
                </div>
              </div>

              <div className="bento-card bg-gradient-to-br from-teal-50 to-cyan-50">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0" style={{ background: 'linear-gradient(135deg, #28D9C3, #20C8B4)' }}>
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Response Time</h3>
                    <p className="text-slate-600">Within 24 hours on business days</p>
                  </div>
                </div>
              </div>

              <div className="bento-card bg-gradient-to-br from-slate-50 to-gray-50">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-700 to-gray-700 flex items-center justify-center shadow-lg flex-shrink-0">
                    <Globe className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Global Reach</h3>
                    <p className="text-slate-600">Remote-first, working with teams across industries and regions</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right Column - Metrics & Benefits */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="bento-card">
                <h2 className="text-3xl font-bold mb-6 gradient-text">Why Teams Work With Us</h2>
              </div>

              <div className="bento-card bg-gradient-to-br from-purple-50 to-violet-50 text-center">
                <div className="text-5xl font-bold gradient-text mb-2">{METRICS.yearsExperience}</div>
                <div className="text-slate-700 font-semibold mb-1">Years of Excellence</div>
                <div className="text-sm text-slate-600">Architecting enterprise-grade systems</div>
              </div>

              <div className="bento-card bg-gradient-to-br from-teal-50 to-cyan-50 text-center">
                <div className="text-5xl font-bold mb-2" style={{ color: '#28D9C3' }}>{METRICS.customSolutions}</div>
                <div className="text-slate-700 font-semibold mb-1">Solutions Delivered</div>
                <div className="text-sm text-slate-600">Across fintech, healthcare, e-commerce, SaaS and other domains</div>
              </div>

              <div className="bento-card">
                <h3 className="font-bold text-lg mb-4 text-[#4C3362]">Partnership Benefits</h3>
                <ul className="space-y-3">
                  {[
                    'Strategic consultation complimentary',
                    'Comprehensive technical proposals',
                    'Transparent cost structures',
                    'Flexible engagement models',
                    'Direct engineering access',
                    'Continuous progress visibility'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-slate-700 text-sm">
                      <CheckCircle className="w-5 h-5 text-[#4C3362] flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ContactPage;
